import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { useGetAlunosQuery } from '@/graphql/pessoas/queries.generated';
import type { AlunoFieldsFragment } from '@/graphql/pessoas/fragments.generated';

/**
 * Tipo para representar os dados do aluno logado
 */
export type AlunoLogado = AlunoFieldsFragment;

/**
 * Tipo para matrícula ativa
 */
export interface MatriculaAtiva {
  id: number;
  status: string;
  dataMatricula?: string | null;
  turma?: {
    id: number;
    nome: string;
    turno?: string | null;
    anoLetivo?: {
      id: number;
      ano: number;
    } | null;
    escola?: {
      id: number;
      nomeFantasia?: string | null;
      nomeOficial?: string | null;
    } | null;
    curso?: {
      id: number;
      nome: string;
    } | null;
  } | null;
}

interface AlunoContextData {
  aluno: AlunoLogado | null;
  matriculaAtiva: MatriculaAtiva | null;
  turmaAtual: MatriculaAtiva['turma'] | null;
  escolaAtual: MatriculaAtiva['turma'] extends { escola: infer E } ? E : null;
  anoLetivoAtual: MatriculaAtiva['turma'] extends { anoLetivo: infer A } ? A : null;
  isLoading: boolean;
  error: Error | null;
  refreshData: () => void;
}

const AlunoContext = createContext<AlunoContextData | undefined>(undefined);

interface AlunoProviderProps {
  children: React.ReactNode;
}

/**
 * Provider que gerencia o estado do aluno logado
 * Busca dados do aluno associado ao usuário autenticado
 */
export function AlunoProvider({ children }: AlunoProviderProps) {
  const { user, hasRole } = useAuth();

  // Obter pessoaId do usuário logado
  const pessoaId = useMemo(() => {
    return user?.pessoa?.id ? Number(user.pessoa.id) : null;
  }, [user?.pessoa?.id]);

  // Skip query se não tem pessoaId OU se o vínculo ativo não é ALUNO
  const isAluno = hasRole('ALUNO');
  const skipQuery = !pessoaId || !isAluno;

  const { data, loading, error, refetch } = useGetAlunosQuery({
    variables: {
      where: {
        pessoaId: pessoaId,
      },
      pagination: {
        page: 1,
        limit: 1,
      },
    },
    skip: skipQuery,
    fetchPolicy: 'cache-and-network',
  });

  // Extrair dados do aluno
  const aluno = useMemo((): AlunoLogado | null => {
    if (!data?.alunos?.data || data.alunos.data.length === 0) {
      return null;
    }
    return data.alunos.data[0] as AlunoLogado;
  }, [data]);

  // Obter matrícula ativa
  const matriculaAtiva = useMemo((): MatriculaAtiva | null => {
    if (!aluno?.matriculas) return null;
    const ativa = aluno.matriculas.find((m) => m.status === 'ATIVA');
    return ativa ? (ativa as unknown as MatriculaAtiva) : null;
  }, [aluno]);

  // Obter turma atual
  const turmaAtual = useMemo(() => {
    return matriculaAtiva?.turma || null;
  }, [matriculaAtiva]);

  // Obter escola atual
  const escolaAtual = useMemo(() => {
    return turmaAtual?.escola || null;
  }, [turmaAtual]);

  // Obter ano letivo atual
  const anoLetivoAtual = useMemo(() => {
    return turmaAtual?.anoLetivo || null;
  }, [turmaAtual]);

  // Função para atualizar dados
  const refreshData = useCallback(() => {
    if (!skipQuery) refetch();
  }, [skipQuery, refetch]);

  // Converter erro do Apollo para Error
  const errorState = useMemo(() => {
    if (error) {
      return new Error(error.message || 'Erro ao carregar dados do aluno');
    }
    return null;
  }, [error]);

  const value: AlunoContextData = {
    aluno,
    matriculaAtiva,
    turmaAtual,
    escolaAtual: escolaAtual as AlunoContextData['escolaAtual'],
    anoLetivoAtual: anoLetivoAtual as AlunoContextData['anoLetivoAtual'],
    isLoading: loading,
    error: errorState,
    refreshData,
  };

  return (
    <AlunoContext.Provider value={value}>
      {children}
    </AlunoContext.Provider>
  );
}

// Valor padrão para quando o provider não está disponível ou o usuário não é aluno
const defaultAlunoContext: AlunoContextData = {
  aluno: null,
  matriculaAtiva: null,
  turmaAtual: null,
  escolaAtual: null as AlunoContextData['escolaAtual'],
  anoLetivoAtual: null as AlunoContextData['anoLetivoAtual'],
  isLoading: false,
  error: null,
  refreshData: () => {},
};

/**
 * Hook para acessar o contexto do aluno
 * Retorna defaults seguros quando fora do provider (permite uso global)
 */
export function useAlunoContext(): AlunoContextData {
  const context = useContext(AlunoContext);
  if (context === undefined) {
    return defaultAlunoContext;
  }
  return context;
}
